import * as yup from 'yup';
import { Date as Date$1 } from 'sugar-date';
import { unflatten, flatten } from 'flat';
import { isEqual, get, isMatch } from 'lodash';

var ValidatorType = {
  IS: 'is',
  NOT: 'not',
  AFTER: 'after',
  PAST: 'past',
  CONTEXT: 'context',
  ANY: 'any',
  VALID: 'valid',
  INVALID: 'invalid',
  NEGATE: 'negate',
  SAME: 'same',
  ONE_OF: 'oneOf',
  NOT_ONE_OF: 'notOneOf',
  DIFFERENT: 'different',
  REQUIRED: 'required',
  NOT_REQUIRED: 'notRequired',
  EMAIL: 'emailFormat',
  URL: 'urlFormat',
  POSTAL: 'postal',
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
  INTEGER: 'integer',
  BEFORE: 'before',
  FUTURE: 'future',
  MATCHES: 'matches',
  PHONE: 'phone',
  INCLUDES: 'includes',
  APPLICATION: 'application',
  SUBSET: 'subset',
  SUPERSET: 'superset',
  DEFAULT: 'default',
  SHAPE: 'shape',
};

const DEFAULT_YUP_CONTEXT_PREFIX = '$';
const DEFAULT_CONFIGURABLE_CONTEXT_PREFIX = '#';

let CONTEXT_PREFIX;
const setContextPrefix = (
  prefix = DEFAULT_CONFIGURABLE_CONTEXT_PREFIX
) => {
  if (typeof CONTEXT_PREFIX !== 'undefined') {
    throw new Error(
      `Validation Context Prefix has already been set to ${CONTEXT_PREFIX}`
    );
  }
  return (CONTEXT_PREFIX = prefix);
};
const getContextPrefix = () => CONTEXT_PREFIX || setContextPrefix();
const makeRef = what =>
  yup.ref(
    what.replace(
      new RegExp(`^${getContextPrefix()}`),
      DEFAULT_YUP_CONTEXT_PREFIX
    )
  );
const getRef = what => (what && what.ref ? makeRef(what.ref) : what);
const isRef = what => what && what.ref;
const resolveOptionalRef = (resolver, value) => resolver(getRef(value));
const field = what => (isRef(what) ? what.ref : what);

const addValidator = (name, fn, schemaType = yup.mixed, message) => {
  yup.addMethod(schemaType, name, function(...args) {
    const nextargs = [...args, message];
    return this.test(name, message, function(value) {
      return fn.apply(this, [value, ...nextargs, this.options]);
    });
  });
};

/**
 * Determines if a Condition object matches based on values
 *
 * @param {value} Condition - consisting of schemas, how (default every) and sync (default true)
 * @param {values} Object of key-value pairs
 * @param {context} context if applicable
 * @return {boolean | Promise<boolean>}
 *
 */
const matches = ({ schemas, sync = true }, values, context = {}) => {
  // collect all the validations by calling isValid(Sync) on each schema
  const validations = schemas.map(schema =>
    schema[sync ? 'isValidSync' : 'isValid'](values, { context })
  );

  return sync
    ? validations.some(v => !!v)
    : Promise.all(validations).then(([...args]) => args.some(v => !!v));
};

const createDefaultValidator = ({ default: def }) =>
  def ? { default: def } : undefined;

/**
 *
 * Takes a SchemaValidator (or array of them) and returns the Schemas
 *
 * @param {Object | Array} SchemaValidator[]
 * @param {schemas[]}
 */
const createSchemas = (schemaValidator, types, parentSchema) =>
  (Array.isArray(schemaValidator) ? schemaValidator : [schemaValidator]).map(
    s => createSchema(s, types, parentSchema)
  );

/**
 * Creates schema from a SchemaValidator
 *
 * {
 *    "first_name": {
 *        "not":"",
 *        "oneOf": [1,2,3,4,5],
 *        "notOneOf": {"value":[6,7,8,{ref:'last_name'}],"message":"locale_id" }
 *        "required": true,
 *     },
 *    "last_name": {
 *        ....
 *    }
 * }
 *
 *
 * @param {SchemaValidator}
 * @param {Object} types - Map of field names to SchemaType
 * @param {ObjectSchema} schema - a yup.object() schema
 * @return {schema}
 *
 */
const createSchema = (schemaValidator, schemaTypes = {}, parentSchema) =>
  yup.object(
    Object.entries(schemaValidator).reduce(
      (acc, [key, val]) => ({
        ...acc,
        [key]: attachValidators(
          val,
          schemaTypes[key] && yup[schemaTypes[key]](),
          schemaTypes,
          parentSchema,
          key
        ),
      }),
      {}
    )
  );

/**
 * Attaches validators from a FieldDescriptorValidator to a schema
 *
 * {
 *    "not":"",
 *    "oneOf": [1,2,3,4,5],
 *    "notOneOf": {"value":[6,7,8,{ref:'last_name'}],"message":"locale_id"}
 *    "required": true,
 * }
 *
 * @param {fieldDescriptorValidator} FieldDescriptorValidator
 * @param {schema} optional - existing schema to attach validators to
 * @return {schema}
 *
 */
const attachValidators = (
  fieldDescriptorValidator,
  schema,
  types,
  parentSchema,
  field
) =>
  Object.keys(fieldDescriptorValidator).reduce((schema, type) => {
    const validator = fieldDescriptorValidator[type];
    return attachValidator(
      {
        type,
        value: valueForValidation(validator),
        message: validator.message || undefined,
        parentSchema,
        field,
        options: validator.options || {},
      },
      schema || (validator.schema ? yup[validator.schema]() : undefined),
      types
    );
  }, schema);

/**
 * Attaches a validator to a schema
 *
 * @param {validator}
 * @param {schema} optional - existing schema to attach validator to
 * @return {schema}
 *
 */
const attachValidator = (validator, schema = yup.mixed(), types) => {
  const { type, value, message, parentSchema, field, options } = validator;
  const messageFn = () => message;

  switch (type) {
    // not a validator, but we need to have the ability
    // to add a default value for a schema
    case ValidatorType.DEFAULT:
      return schema.default(value);

    case ValidatorType.ANY:
      return schema.notRequired();

    // the following are "negate-able" types
    // i.e. in the validator map the "value" for the validator is a boolean
    // e.g.
    // {
    //    "emailFormat": true,
    //    "past": false, // date SHOULD NOT be in the past
    //    "future": true, // date SHOULD be in the future
    // }
    //
    // Word of advice, try to use true only, the following will make your head hurt
    // {
    //   "notRequired": false
    // }
    //
    //
    case ValidatorType.POSTAL:
      return value
        ? schema.postal(messageFn)
        : schema.negate('notPostal', yup.string().postal(), messageFn);

    case ValidatorType.PHONE:
      return value
        ? schema.phone(messageFn)
        : schema.negate('notPhone', yup.string().phone(), messageFn);

    case ValidatorType.EMAIL:
      return value
        ? schema.emailFormat(messageFn)
        : schema.negate('notEmail', yup.string().emailFormat(), messageFn);

    case ValidatorType.URL:
      return value
        ? schema.urlFormat(message)
        : schema.negate('notUrl', yup.string().urlFormat(), messageFn);

    case ValidatorType.INTEGER:
      return value
        ? schema.integer(message)
        : schema.negate('notInteger', yup.number().email(), messageFn);

    case ValidatorType.POSITIVE:
      return value
        ? schema.positive(message)
        : schema.negate('notPositive', yup.number().positive(), messageFn);

    case ValidatorType.NEGATIVE:
      return value
        ? schema.negative(message)
        : schema.negate('notNegative', yup.number().negative(), messageFn);

    case ValidatorType.PAST:
      return value ? schema.past(messageFn) : schema.future(messageFn);

    case ValidatorType.FUTURE:
      return value ? schema.future(messageFn) : schema.past(messageFn);

    case ValidatorType.REQUIRED:
      return value ? schema.required(messageFn) : schema.notRequired(messageFn);

    case ValidatorType.NOT_REQUIRED:
      return !value
        ? schema.required(messageFn)
        : schema.notRequired(messageFn);

    case ValidatorType.CONTEXT:
      return schema.context({ ...value, types });

    // special validators, can only be run in conditions/context validators
    case ValidatorType.VALID:
      if (!parentSchema) {
        throw new Error(
          'Valid field validator can only be used in a condition in the context of a parent schema'
        );
      }
      return value
        ? schema.valid(field, parentSchema, messageFn)
        : schema.negate(
            'notValid',
            yup.mixed().valid(),
            field,
            parentSchema,
            messageFn
          );

    case ValidatorType.INVALID:
      if (!parentSchema) {
        throw new Error(
          'Invalid field validator can only be used in a condition in the context of a parent schema'
        );
      }
      return !value
        ? schema.valid(field, parentSchema, messageFn)
        : schema.negate(
            'notValid',
            yup.mixed().valid(),
            field,
            parentSchema,
            messageFn
          );

    case ValidatorType.MATCHES:
      return schema.matches(
        new RegExp(...(Array.isArray(value) ? value : [value])),
        {
          message: messageFn,
          excludeEmptyString: true,
          ...options,
        }
      );

    case ValidatorType.IS:
    case ValidatorType.NOT:
    case ValidatorType.SAME:
    case ValidatorType.DIFFERENT:
    case ValidatorType.BEFORE:
    case ValidatorType.AFTER:
    case ValidatorType.NOT_ONE_OF:
    case ValidatorType.ONE_OF:
    default:
      try {
        return typeof value !== 'undefined'
          ? schema[type](value, messageFn, options)
          : schema[type](messageFn, options);
      } catch (err) {
        throw new Error(`Failed to create validator of type ${type}`);
      }
  }
};

const valueForValidation = validation =>
  validation && Object.prototype.hasOwnProperty.call(validation, 'value')
    ? validation.value
    : validation;

const schemaDependencies = validations => [
  ...new Set(
    Object.entries(validations || {}).reduce((acc, [type, validator]) => {
      const value = valueForValidation(validator);
      if (isRef(value)) return acc.concat(value.ref);
      if (Array.isArray(value))
        return acc.concat(value.filter(v => isRef(v)).map(({ ref }) => ref));
      if ([ValidatorType.SAME, ValidatorType.DIFFERENT].includes(type))
        return acc.concat(value);

      if (type === ValidatorType.CONTEXT)
        return acc.concat(conditionDependencies(validator));
      return acc;
    }, [])
  ),
];

// get all dependencies from a when
const conditionDependencies = ({ when } = { when: [] }) => [
  ...(Array.isArray(when) ? when : [when]).reduce(
    (acc, schema) => Object.keys(schema).map(k => acc.add(k)) && acc,
    new Set()
  ),
];

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
};

// yup doesn't have built-in negation, but it's easy enough to do our own
const negate = function(against, type, testSchema, message) {
  const { parent, context, sync } = this.options;
  const valid = testSchema[sync ? 'isValidSync' : 'isValid'](
    resolveOptionalRef(this.resolve, against),
    {
      parent,
      context,
    }
  );

  const done = valid =>
    !against || !valid ? true : this.createError({ type, message });
  return sync ? done(valid) : valid.then(done);
};

const is = function(against, compare, message) {
  const what = resolveOptionalRef(this.resolve, compare);
  return (
    against === what ||
    this.createError({
      message,
      params: { what, field: isRef(compare) ? field(compare) : undefined },
    })
  );
};

const not = function(against, compare, message) {
  const what = resolveOptionalRef(this.resolve, compare);
  return (
    against !== what ||
    this.createError({
      message,
      params: { what, field: isRef(compare) ? field(compare) : undefined },
    })
  );
};

const same = function(against, compare, message) {
  const what = this.resolve(makeRef(compare));
  return (
    against === what ||
    this.createError({
      message,
      params: { what, field: field(compare) },
    })
  );
};

const different = function(against, compare, message) {
  const what = this.resolve(makeRef(compare));
  return (
    against !== what ||
    this.createError({
      message,
      params: { what, field: isRef(compare) ? compare.ref : compare },
    })
  );
};

const before = function(against, compare, message) {
  const what = Date$1.create(resolveOptionalRef(this.resolve, compare));
  return (
    against <= what ||
    this.createError({
      message,
      params: { what, field: isRef(compare) ? field(compare) : undefined },
    })
  );
};

const after = function(against, compare, message) {
  const what = Date$1.create(resolveOptionalRef(this.resolve, compare));
  return (
    against >= what ||
    this.createError({
      message,
      params: { what, field: isRef(compare) ? field(compare) : undefined },
    })
  );
};

const includes = function(against, compare, message) {
  const what = resolveOptionalRef(this.resolve, compare);
  return (
    (against && against.includes(what)) ||
    this.createError({
      message,
      params: { what, field: isRef(compare) ? field(compare) : undefined },
    })
  );
};

// we've created our own oneOf/notOneOf to better deal with
// error refs in our configuration language
const oneOf = function(against, c, message) {
  const compare = resolveOptionalRef(this.resolve, c);
  const what = compare.map(c => resolveOptionalRef(this.resolve, c));
  return (
    what.includes(against) ||
    this.createError({
      message,
      params: { what, options: what, message },
    })
  );
};

const shape = function(against, c, message) {
  return createSchema(c).isValidSync(against) || this.createError(message);
};

// wrap email and URL yup validators so they have a "type"
const email = function(against, message) {
  return (
    yup
      .string()
      .email()
      .isValidSync(against) || this.createError({ message })
  );
};

const postal = function(against, message) {
  return (
    !against ||
    against.match(/^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$/) ||
    this.createError({ message })
  );
};

const phone = function(against, message) {
  return !against || against.match(/^\d{10}$/) || this.createError({ message });
};

const url = function(against, message) {
  return (
    yup
      .string()
      .url()
      .isValidSync(against) || this.createError({ message })
  );
};

const notOneOf = function(against, c, message) {
  const compare = resolveOptionalRef(this.resolve, c);
  const what = compare.map(c => resolveOptionalRef(this.resolve, c));
  return (
    !what.includes(against) ||
    this.createError({
      message,
      params: { what, options: what },
    })
  );
};

const future = function(against, message) {
  return against > today() || this.createError({ message });
};

const past = function(against, message) {
  return against < today() || this.createError({ message });
};

const valid = function(_, field, parentSchema, message) {
  const { sync, parent, context } = this.options;
  const method = sync ? 'validateSyncAt' : 'validateAt';
  const error = () => this.createError({ message });
  try {
    const result = parentSchema[method](field, parent, {
      parent,
      context,
      sync,
    });

    return sync || result.then(() => true).catch(error);
  } catch (e) {
    error();
  }
};

/**
 * Context validator looks similar to conditional but is much more powerful
 * A context validator validates schemas against the PARENT of the
 * subject field and validates the resultant then/otherwise against the subject
 *
 * The purpose of the context validator is to alleviate multi-level nested
 * conditional validators that can become unwieldly to manage
 *
 * when - SchemaValidator | SchemaValidator[]
 * how - some | every (default every)
 * then - Validators
 * otherwise - Validators
 *
 * There are two main differences between context and conditional validators
 *
 * 1. A context validator may specify an array of SchemaValidators (and a how)
 * 2. ALL sibling fields can be ref'd in validator's, regardless of if they were declared as keys in the SchemaValidator(s)
 *
 *
 * Example:
 *
 *   "context": {
 *       "when": [
 *           {
 *               "last_name":{"required":true}
 *           },
 *           {
 *               "middle_name":{"pattern":"ABC"}
 *           }
 *       ],
 *       "how":"some",
 *       "then": {
 *           "min":{"value":20,"message":"Must be at least 20 characters in this context"}
 *       }
 *   }
 *
 *
 *  Context validators are exclusive - there can only be one context validator at a time HOWEVER
 *  because the validate a new schema (not the original) it will not "obey" exclusive tests on the original schema
 *
 *
 */

// Note: the funny looking closure is so this could live separately from attachValidators
// it's worthy of a separate file and requires a large test suite

var context = (attachValidators, matches, createSchemas) => {
  function done(which, against, types) {
    // nothing to do
    if (!which) {
      return true;
    }

    const { resolve, path, parent, schema } = this;
    const { sync = false, context, abortEarly } = this.options;

    // we are going to validate against a brand new schema at this point
    // DO NOT attach then/otherwise validators to the old schema
    // because then we'll end up duplicating error messages
    return attachValidators(which, yup[schema.describe().type](), types)[
      sync ? 'validateSync' : 'validate'
    ](resolve(against), {
      parent,
      context,
      abortEarly,
      path,
    });
  }

  return function(against, { when, then, otherwise, types }) {
    const { sync = false, parent, context } = this.options;

    const valid = matches(
      { schemas: createSchemas(when, types), sync },
      parent,
      context
    );
    const d = valid =>
      done.call(this, valid ? then : otherwise, against, types);
    return sync ? d(valid) : valid.then(d).then(() => true);
  };
};

const APPLICATION_VALIDATOR_UCIAVAILABLE = 'uciAvailable';
const ERROR_UCIAVAILABLE_ALREADY_ENROLLED = 'uciAlreadyEnrolled';
const ERROR_UCIAVAILABLE_WRONG_PROGRAM = 'uciWrongProgram';
const ERROR_UCIAVAILABLE_DOESNT_EXIST = 'uciDoesntExist';

const uciAvailable = () => ({
  test: [
    {
      isEnrolled: { is: false },
      programId: { same: '$programId' },
      adjudicationStatus: { is: 'ACTIVE' },
    },
    {
      isEnrolled: { is: false },
      transferPrograms: {
        includes: { value: { ref: '$programId' }, schema: 'array' },
      },
      adjudicationStatus: { is: 'ACTIVE' },
    },
  ],
  errors: [
    {
      when: {
        isEnrolled: { is: true },
      },
      error: {
        params: { type: ERROR_UCIAVAILABLE_ALREADY_ENROLLED },
      },
    },
    {
      when: {
        status: { is: 404 },
      },
      error: {
        params: { type: ERROR_UCIAVAILABLE_DOESNT_EXIST },
      },
    },
    {
      when: {
        programId: { different: '$programId' },
      },
      error: {
        params: { type: ERROR_UCIAVAILABLE_WRONG_PROGRAM },
      },
    },
  ],
});

// preconfigured validators so it's easier

var applicationValidators = /*#__PURE__*/Object.freeze({
  __proto__: null,
  APPLICATION_VALIDATOR_UCIAVAILABLE: APPLICATION_VALIDATOR_UCIAVAILABLE,
  ERROR_UCIAVAILABLE_ALREADY_ENROLLED: ERROR_UCIAVAILABLE_ALREADY_ENROLLED,
  ERROR_UCIAVAILABLE_WRONG_PROGRAM: ERROR_UCIAVAILABLE_WRONG_PROGRAM,
  ERROR_UCIAVAILABLE_DOESNT_EXIST: ERROR_UCIAVAILABLE_DOESNT_EXIST,
  uciAvailable: uciAvailable
});

const applicationValidatorMap = new Map();

const addApplicationValidator = (type, handler, validator) => {
  const existing = applicationValidatorMap.get(type);
  if (existing && existing !== handler)
    throw new Error(
      `Application validator ${type} has already been registered with a different handler`
    );

  applicationValidatorMap.set(type, { handler, validator });
  return () => applicationValidatorMap.delete(type);
};

const runTest = (what = [], values = {}, context = {}) =>
  (Array.isArray(what) ? what : [what]).find(({ how, when }) =>
    matches({ how, schemas: createSchemas(when) }, values, context)
  );

const extendOptionalArray = what =>
  Array.isArray(what) ? what : what ? [what] : [];

const extend = (
  { test, errors, error } = {},
  extendTests,
  extendErrors,
  defaultError
) => ({
  test: [...extendOptionalArray(test), ...extendOptionalArray(extendTests)],
  errors: [
    ...extendOptionalArray(extendErrors),
    ...extendOptionalArray(errors),
  ],
  error: defaultError || error,
});

const runValidator = async function(
  against,
  { type, test, errors, error, options }
) {
  let testAgainst;
  const { handler, validator: definedValidator = {} } =
    applicationValidatorMap.get(type) || {};
  if (!handler) {
    throw new Error(`No application validator for type ${type}`);
  }

  // we can provide extensible pre-configured validators
  const preconfigured = applicationValidators[type];
  const validator = {
    type,
    ...extend(
      preconfigured ? preconfigured(options) : definedValidator,
      test,
      errors,
      error
    ),
  };

  if (!validator.test) {
    throw new Error(`Application validator ${type} must supply a test`);
  }

  try {
    testAgainst = await handler(against, validator, this.parent);
  } catch (error) {
    testAgainst = error;
  }

  // pass :)
  if (runTest({ when: validator.test }, testAgainst, this.parent)) {
    return true;
  }

  // fail :( - try and find the error message or use the default validator.error
  const { message, params } =
    (runTest(validator.errors, testAgainst, this.parent) || {}).error ||
    validator.error ||
    {};

  return this.createError({
    message: message || '',
    params: {
      ...testAgainst,
      type,
      ...(params || {}),
    },
  });
};

const application = async function(against, validator) {
  // an "application" validator can be a single validator - or multiple validators
  // that will be run sequentially
  return (Array.isArray(validator) ? validator : [validator]).reduce(
    async (acc, v) =>
      (await acc) === true ? runValidator.call(this, against, v) : true,
    Promise.resolve(true)
  );
};

/**
 * This set of functions takes an object of one structure and transforms it to a different
 * structure using a configuration object (ValueTransforms)
 *
 * ValueTransforms is an object whose keys match the keys of the object it is transforming.
 * It's values can contain the following transformation properties
 * {
 *    name?: string (one of name or path must be defined)
 *    path?: object accessor - e.g. path.to.value
 *    metaValue?: object or array (requires an accessor)
 *    accessor?: (only makes sense with a metaValue)
 *    fieldPathType?: 'array' - array is the only possible value, use where applicable
 *    ignore?: boolean ignore this transform
 * }
 *
 *
 *
 * It can convert this value:
 * {
 *    name: 'Adam'
 *    terms: 'true',
 *    communication: 'false'
 * }
 *
 * using this ValueTransforms object:
 * {
 *    foo: {
 *      name: 'name',
 *      path: 'patient.firstName'
 *    },
 *    baz: {
 *       name: 'terms',
 *       path: 'patient.enrollmentOptions',
 *       fieldPathType: 'array',
 *       metaValue: {
 *           name: 'CONTRAVE_TERMS_AND_CONDITIONS_V1'
 *       },
 *       accessor: 'value'
 *    },
 *    qux: {
 *      name: 'communication'
 *      path: 'patient.enrollmentOptions',
 *      fieldPathType: 'array',
 *      metaValue: {
 *          name: 'pmvx-patient-main-contact'
 *      },
 *      accessor: 'value'
 *    }
 * }
 *
 * to this:
 *
 * {
 *    patient: {
 *      firstname: 'Adam'
 *      enrollmentOptions: [
 *        {
 *          name: 'CONTRAVE_TERMS_AND_CONDITIONS_V1',
 *          value: 'true'
 *        },
 *        {
 *          name: 'pmvx-patient-main-contact',
 *          value: 'false'
 *        }
 *      ]
 *    }
 * }
 *
 */

const transformsForProp = (prop, propValue, ts, raw) =>
  Object.keys(ts).reduce(
    (acc, key) =>
      propValue && ts[key][prop] === propValue && (!ts[key].disabled || raw)
        ? acc.concat(ts[key])
        : acc,
    []
  );

// names must be unique OR completely share transformation properties (metaValue, accessor, etc)
// this is for the case of individual radio buttons which share the same name but don't have the same top level identifier
const transformForName = (name, ts, raw, ignorePathless) =>
  transformsForProp('name', name, ts, raw).reduce((acc, t) => {
    if (t.disabled && raw) {
      return acc;
    }

    if (!t.path && ignorePathless) {
      return acc;
    }

    // check here to make sure the transformation properties are the same as acc
    if (
      acc &&
      (t.path !== acc.path ||
        t.accessor !== acc.accessor ||
        !isEqual(t.metaValue, acc.metaValue))
    ) {
      throw new Error(
        `Multiple transforms found for name ${name} with different transformation properties!`
      );
    }

    return t;
  }, null);

const transformsForPath = (path, ts, raw, ignorePathless) =>
  transformsForProp('path', path, ts, raw).reduce((acc, t) => {
    const forName = transformForName(t.name, ts, raw, ignorePathless);
    return !forName || acc.find(_t => _t.name === t.name) ? acc : [...acc, t];
  }, []);

const doTransform = (t, value) =>
  !t.accessor
    ? value
    : unflatten({ ...(t.metaValue || {}), [t.accessor]: value });

const getTransformedValue = (t, values, ts, raw, ignorePathless) => {
  const all = t.path ? transformsForPath(t.path, ts, raw, ignorePathless) : [t];
  return all.length === 1 && t.fieldPathType !== 'array'
    ? doTransform(all[0], values[all[0].name])
    : all
        .map(_t => ({ transform: _t, value: doTransform(_t, values[_t.name]) }))
        .filter(
          ({ transform, value }) =>
            !rejectValue(
              transform,
              transform.accessor ? get(value, transform.accessor) : value,
              transform.ignoreUndefined
            )
        )
        .map(({ value }) => value);
};

const to = (values, ts, raw = false, ignorePathless = true) =>
  unflatten(
    Object.keys(values).reduce((acc, fieldName) => {
      const t = transformForName(fieldName, ts, raw, ignorePathless);
      return !t
        ? acc
        : {
            ...acc,
            [t.path || t.name]: getTransformedValue(
              t,
              values,
              ts,
              raw,
              ignorePathless
            ),
          };
    }, {})
  );

const merge = (...args) => unflatten(mergeFlat(...args));

const mergeFlat = (newest = {}, ...other) => ({
  ...other.reduce((acc, o = {}) => flatten({ ...acc, ...o }), {}),
  ...flatten(newest),
});

const initialValueFromArray = (value, t) =>
  value.find(v => t.metaValue && isMatch(v, t.metaValue));

const rejectValue = (t, value, considerUndefined = false) =>
  (considerUndefined && typeof value === 'undefined') ||
  (t.reject || []).includes(value) ||
  (t.compact && !value);

const from = (values, ts) =>
  Object.keys(ts).reduce((acc, key) => {
    const t = ts[key];
    const initialValue = get(values, `${t.path || t.name}`);

    if (typeof initialValue === 'undefined') {
      return acc;
    }

    const transforms = transformsForPath(t.path, ts, true, false);

    // if the initialValue is an array, we MAY need to split it up and reassign it to the appropriate transforms (fields)
    // if the current transform has a pathType of array, then the actual value for the transform is one of the items
    // in the initialValue array. Alternatively, if the pathType is not array but multiple transforms exist with the
    // same path, then the value for the transform is, again, one of the items in the initialValue array.
    const needsUnwinding =
      Array.isArray(initialValue) &&
      (t.fieldPathType === 'array' || transforms.length > 1);

    if (!needsUnwinding) {
      return {
        ...acc,
        [t.name]: t.accessor ? get(initialValue, t.accessor) : initialValue,
      };
    }

    // try and match the value from the array based on the metaValue of the transform
    // fallback to just using the value at the index that this transform is
    // maybe we should throw an error instead?
    const val = initialValueFromArray(initialValue, t) || {};
    const accessedVal = t.accessor ? get(val, t.accessor) : val;

    return rejectValue(t, accessedVal)
      ? acc
      : {
          ...acc,
          [t.name]: accessedVal,
        };
  }, {});

// add our custom validators
addValidator(ValidatorType.IS, is);
addValidator(ValidatorType.INCLUDES, includes, yup.array);
addValidator(ValidatorType.INCLUDES, includes, yup.string);
addValidator(ValidatorType.APPLICATION, application);
addValidator(ValidatorType.NOT, not);
addValidator(ValidatorType.SAME, same);
addValidator(ValidatorType.DIFFERENT, different);
addValidator(ValidatorType.ONE_OF, oneOf);
addValidator(ValidatorType.NOT_ONE_OF, notOneOf);
addValidator(ValidatorType.NEGATE, negate);
addValidator(ValidatorType.VALID, valid);
addValidator(ValidatorType.BEFORE, before, yup.date);
addValidator(ValidatorType.AFTER, after, yup.date);
addValidator(ValidatorType.FUTURE, future, yup.date);
addValidator(ValidatorType.PAST, past, yup.date);
addValidator(ValidatorType.EMAIL, email, yup.string);
addValidator(ValidatorType.URL, url, yup.string);
addValidator(ValidatorType.PHONE, phone, yup.string);
addValidator(ValidatorType.POSTAL, postal, yup.string);
addValidator(ValidatorType.SHAPE, shape, yup.mixed);
addValidator(
  ValidatorType.CONTEXT,
  context(attachValidators, matches, createSchemas),
  yup.mixed
);

export { APPLICATION_VALIDATOR_UCIAVAILABLE, ERROR_UCIAVAILABLE_ALREADY_ENROLLED, ERROR_UCIAVAILABLE_DOESNT_EXIST, ERROR_UCIAVAILABLE_WRONG_PROGRAM, ValidatorType, addApplicationValidator, addValidator, createDefaultValidator, createSchema, createSchemas, from, matches, merge, mergeFlat, schemaDependencies, setContextPrefix, to, uciAvailable, valueForValidation };
